import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import {
  Organization,
  OrganizationDetail,
  OrganizationMember,
  InviteMemberPayload,
  InviteMemberResponse,
  PatchMemberPayload,
  PaginatedResponse,
} from './types';

/**
 * Fetch all organizations the caller belongs to (owner, admin, member, or pending invite)
 */
export function useOrganizations() {
  return useQuery<Organization[]>({
    queryKey: ['organizations'],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<Organization> | Organization[]>(
        '/v2/organizations/'
      );
      if (Array.isArray(res.data)) return res.data;
      return res.data?.results || [];
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch detailed stats for a specific organization (owner/admin only)
 */
export function useOrganizationDetail(orgId?: string) {
  return useQuery<OrganizationDetail>({
    queryKey: ['organization', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const res = await api.get<OrganizationDetail>(`/v2/organizations/${orgId}/`);
      return res.data;
    },
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch member roster for an organization (owner/admin only)
 */
export function useOrganizationMembers(orgId?: string) {
  return useQuery<OrganizationMember[]>({
    queryKey: ['organization-members', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const res = await api.get<PaginatedResponse<OrganizationMember> | OrganizationMember[]>(
        `/v2/organizations/${orgId}/members/`
      );
      if (Array.isArray(res.data)) return res.data;
      return res.data?.results || [];
    },
    enabled: Boolean(orgId),
    staleTime: 30 * 1000,
  });
}

/**
 * Invite a new member to an organization via email
 */
export function useInviteOrganizationMember() {
  const queryClient = useQueryClient();

  return useMutation<
    InviteMemberResponse,
    Error,
    { orgId: string; payload: InviteMemberPayload }
  >({
    mutationFn: async ({ orgId, payload }) => {
      const res = await api.post<InviteMemberResponse>(
        `/v2/organizations/${orgId}/invite/`,
        payload
      );
      return res.data;
    },
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: ['organization-members', orgId] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

/**
 * Update member role or status (block / remove)
 */
export function useUpdateOrganizationMember() {
  const queryClient = useQueryClient();

  return useMutation<
    OrganizationMember,
    Error,
    { orgId: string; memberId: string; payload: PatchMemberPayload }
  >({
    mutationFn: async ({ orgId, memberId, payload }) => {
      const res = await api.patch<OrganizationMember>(
        `/v2/organizations/${orgId}/members/${memberId}/`,
        payload
      );
      return res.data;
    },
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: ['organization-members', orgId] });
    },
  });
}
